import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from "lodash";
import { CRUDPagination, CRUDPayloadData, CRUDResolverInterface } from './types/crud.types';
import { Filtro } from './filtri/filtri.type';
import { OPERATORI } from './filtri/services/enums/operatori-filtri.enums';
import { FiltriParser } from './filtri/services/parser/filtri-parser';
import { ElencoFiltriService } from './filtri/ElencoFiltri.service';
import { bufferTime, filter, map, switchMap } from 'rxjs/operators';
import { CRUDColumn } from './crud-dom/crud-column';
import { SpreadSheetsExporter } from './exports/spreadsheets/spreadsheets-exporter.class';
import { PDFExporter } from './exports/pdf/pdf-exporter.class';
import { IcsExporter } from './exports/ics/ics-exporter.class';
import { SessionHandler } from './session-handler/session-handler.service';
import { UriHandler } from './uri-handler/uri-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { AppLocaleService } from '@alfercom/common-utils';
//import { AppInfoService } from 'app/core/gui/Services/app-info.service';

const DEFAULT_LIMIT = 20;

@Injectable()
export class CRUDService {
  private spreadSheetsExporter: SpreadSheetsExporter = new SpreadSheetsExporter();
  private pdfExporter: PDFExporter;//
  private icsExporter: IcsExporter = new IcsExporter();

  protected sessionHandler: SessionHandler;
  protected uriHandler: UriHandler;

  serverSideDriver: CRUDResolverInterface<any>;
  serverSideDriverParams: any;

  originData: any[] = []; //rappresenta l'ultimo dataset ricevuto dal server, o l'insieme dei dataset paginati dal server nel momento delle richieste successive
  dataset: any[] = [];
  rows: any[] = [];

  hasBeenInit: boolean = false;
  hasBeenInitSub: Subject<boolean> = new Subject();

  isDataAccumulati: boolean = false; //indica se i dati ottenuti in origin data devono essere accumulati o sovrascritti


  protected searchFilterName: string = "";
  protected filterSearchValue: string = "";
  protected searchFilterValueSub: BehaviorSubject<string> = new BehaviorSubject(null);

  protected useFilters: boolean = false;
  protected filters: any[];
  protected filtersSettings: any[];
  protected hasFiltersSettings: boolean = false;

  protected filtersService: ElencoFiltriService = new ElencoFiltriService();
  protected filterParser: FiltriParser = new FiltriParser();

  protected columns: CRUDColumn[];
  protected exportColumns: CRUDColumn[];
  protected allColumns: CRUDColumn[] = [];

  defaultCRUDSettings: CRUDPayloadData = {
    hasFiltersSettings: this.hasFiltersSettings,
    currentQuery: { limit: DEFAULT_LIMIT },
    dataset: []
  };

  private isLoading: boolean = false;

  private currentQuery: {
    where?: any;
    order?: any;
    limit?: number;
    offset?: number;
  } = {};

  private currentQuerySubject: Subject<{
    where?: any;
    order?: any;
    limit?: number;
    offset?: number;
  }>;

  private isLoadingSubject: BehaviorSubject<boolean>;

  private isServerSideSubject: Subject<boolean>;

  private pagingDataSubject: Subject<CRUDPagination>;

  private rowsSubject: BehaviorSubject<any[]>;

  private pagingData: CRUDPagination = {
    total: 0,
    limit: DEFAULT_LIMIT,
    offset: 0,
    selectionType: 'single'
  };

  lastQuery: any;
  lastQueryParsed: any = null;

  protected exportFormats: { id: string; text: string }[];

  private searchFilterLabelPrefix: string | undefined = 'Cerca: ';

  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected translateService: TranslateService,
    protected appLocale: AppLocaleService
    // @Inject(LOCALE_ID) public locale: string
    //protected guiService : AppInfoService
  ) {

    this.uriHandler = new UriHandler(router);
    this.sessionHandler = new SessionHandler(router);
    this.sessionHandler.setCurrentRoute("CRUD", activatedRoute);
    this.pdfExporter = new PDFExporter();//this.guiService

    this.hasFiltersSettings = this.sessionHandler.has(this.sessionHandler.FILTER_FIELD);

    this.currentQuerySubject = new Subject();
    this.pagingDataSubject = new Subject();
    this.isServerSideSubject = new Subject();
    this.isLoadingSubject = new BehaviorSubject(this.isLoading);
    this.rowsSubject = new BehaviorSubject([]);
  }

  init(
    serverSideDriver: any = null,
    serverSideDriverParams: any = undefined,
    data: CRUDPayloadData = null,
    columns: CRUDColumn[] = null,
    exportColumns: CRUDColumn[] = null,
  ) {
    if (!data) {
      data = this.getCRUDSettings();
      // throw new Error("Payload not setted")
    }
    if (serverSideDriver) {
      this.serverSideDriver = serverSideDriver || null;
    }
    if (serverSideDriverParams) {
      this.serverSideDriverParams = serverSideDriverParams;
    }
    if (data.currentQuery) {
      this.currentQuery = Object.assign(this.currentQuery, data.currentQuery);
    } else {
      if (!this.currentQuery) {
        this.currentQuery = {};
      }
    }
    if (data.dataset) {
      this.dataset = data.dataset;
    }
    if (data.total) {
      this.pagingData.total = data.total;
    }
    if (columns) {
      this.columns = columns;
    }
    if (exportColumns) {
      this.exportColumns = exportColumns;
    }

    const existingPageSettings = this.sessionHandler.getPageSettings();
    if (existingPageSettings) {
      this.pagingData.limit = existingPageSettings.limit;
      this.pagingData.offset = existingPageSettings.offset;
      this.pagingData.orderBy = existingPageSettings.order;
      this.currentQuery.limit = this.pagingData.limit;
      this.currentQuery.offset = this.pagingData.offset;
      this.currentQuery.order = this.pagingData.orderBy;
    } else {
      if (data.limit || data.forceUndefinedLimit) {
        this.pagingData.limit = data.limit;
      }
      if (data.offset) {
        this.pagingData.offset = data.offset;
      }
      if (data.order) {
        this.pagingData.orderBy = data.order;
      }
    }

    this.checkCurrentQuery(data.forceUndefinedLimit);

    this.hasBeenInit = true;
    this.hasBeenInitSub.next(this.hasBeenInit);
    this.emitConfigData();
    this.parseRows();

    const filtriAttivi = this.filters.filter(el => {
      return el.isAttivo;
    });
    const searchFilter = filtriAttivi.find(x => x.nomeCampo == this.searchFilterName);
    if (searchFilter) {
      this.setSearchValue(searchFilter?.extra?.ricerca)
    }
    // this.initExporters();
  }

  checkCurrentQuery(isUndefinedLimitForced = false) {
    if (!this.currentQuery.limit && !isUndefinedLimitForced) {
      this.currentQuery.limit = DEFAULT_LIMIT;
    }
    if (!this.currentQuery.offset || this.currentQuery.offset < 0) {
      this.currentQuery.offset = 0;
    }
    if (!this.currentQuery.order) {
      //this.currentQuery.order = undefined;
    }
  }

  setIsLoading(value: boolean) {
    this.isLoading = value;
    this.isLoadingSubject.next(this.isLoading);
  }

  query(where, goToPageZero: boolean = true) {
    if (goToPageZero) {
      this.currentQuery.offset = 0;
      this.pagingData.offset = 0;
    }
    this.currentQuery.where = where;
  }

  order(orderBy: { prop: string; dir: string }, goToPageZero: boolean = true) {
    if (goToPageZero) {
      this.pagingData.offset = 0;
      this.currentQuery.offset = 0;
    }
    this.pagingData.orderBy = orderBy;
    this.currentQuery.order = orderBy.prop + ' ' + orderBy.dir;
    this.emitConfigData();
    this.runQuery();
  }

  runQuery(input: { getAll?: boolean, returnData?: boolean, forceRunQuery?: boolean } = {}) {
    const { getAll = false, returnData = false, forceRunQuery = false } = input;
    let subRes = new Subject();
    if (this.hasBeenInit) {
      if (!returnData) {
        this.setIsLoading(true);
      }
      let query = Object.assign({}, this.currentQuery);
      if (getAll) {
        if (query.limit) delete query.limit;
        if (query.offset) delete query.offset;
      }
      this.getData(query, returnData, forceRunQuery, subRes);
      return subRes.asObservable();
    }
  }

  /**
   * Centralizzato. Taccone per evitare di fare due chiamate uguali ❤️
   * @param query 
   * @param returnData 
   * @param subRes 
   */
  getData(query: any = {}, returnData, forceRunQuery, subRes) {
    if (query && !query.where) {
      query.where = {};
    }
    const isSameQuery = _.isEqual(query, this.lastQuery);
    const isDatasetEmpty = this.dataset ? this.dataset.length == 0 : true
    if (!isSameQuery || isDatasetEmpty || forceRunQuery) {
      this.serverSideDriver.getData(this.serverSideDriverParams, query).subscribe(res => {
        if (!returnData) {
          this.handleServerResponse(res);
          this.parseRows();
          this.setIsLoading(false);
        } else {
          subRes.next(res);
        }
      });
    } else {
      this.setIsLoading(false);
    }
    this.lastQuery = query;
  }

  handleServerResponse(data) {
    if (data) {
      this.dataset = data.data !== undefined ? data.data : data;
      this.pagingData.total = data.total;
      this.pagingData.offset = data.offset ? data.offset : this.pagingData.offset;
      this.pagingData.limit = data.limit ? data.limit : this.pagingData.limit;
    } else {
      this.dataset = [];
      this.pagingData.total = 0;
      this.pagingData.offset = 0;
      this.pagingData.limit = 20;
    }
    this.emitConfigData();
  }

  setServerSideDriverParams(params) {
    this.serverSideDriverParams = params;
  }

  setServerSideDriver(service) {
    this.serverSideDriver = service;
  }

  getCurrentQuery() {
    return this.currentQuerySubject.asObservable();
  }

  getRows() {
    return this.rowsSubject.asObservable();
  }

  getPagination() {
    return this.pagingDataSubject.asObservable();
  }

  emitConfigData() {
    this.pagingDataSubject.next(this.pagingData);
    this.currentQuerySubject.next(this.currentQuery);
    const { limit, offset, orderBy: order } = this.pagingData;
    this.sessionHandler.setPageSettings({ limit, offset, order });
  }

  /**
   * Ha il compito di fare il parsing delle rows
   */
  private parseRows() {
    this.originData = this.dataset;
    this.rows = this.originData;
    this.rowsSubject.next(this.rows);
  }

  public getIsServerSide() {
    return this.isServerSideSubject.asObservable();
  }

  public getIsLoading() {
    return this.isLoadingSubject.asObservable();
  }

  public changeLimit(limit, runQuery = true) {
    this.currentQuery.limit = limit;
    this.pagingData.limit = limit;
    if (runQuery) {
      this.runQuery();
    }
    this.emitConfigData();
  }

  public changeOffset(offset) {
    this.currentQuery.offset = offset;
    this.pagingData.offset = offset;
    this.runQuery();
    this.emitConfigData();
  }

  public changeOrder(order: { prop: string; dir: string }) {
    this.pagingData.orderBy = order;
    this.runQuery();
    this.emitConfigData();
  }

  public setCRUDSettings(
    settings: {
      limit?: number;
      offset?: number;
      order?: { prop: string; dir: string };
    },
    runQuery: boolean
  ) {
    if (settings.limit) {
      this.currentQuery.limit = settings.limit;
    }
    if (settings.offset !== null && settings.offset !== undefined) {
      this.currentQuery.offset = settings.offset;
    }
    if (settings.order) {
      this.currentQuery.order = settings.order.prop + ' ' + settings.order.dir;
      this.pagingData.orderBy = settings.order;
    }

    if (runQuery) {
      this.runQuery();
    }
  }

  getCurrentQuerySnapshot() {
    return Object.assign({}, this.currentQuery);
  }

  export(type: string, fileName: string, columns: any[] = null) {
    return this.runQuery({ getAll: true, returnData: true, forceRunQuery: true }).pipe(switchMap((res: { data: any[] }) => {
      let allRows = res.data;
      this.initExporter(type, columns);
      if (type == "xls" || type == "xlsx" || type == "csv" || type == "txt") {
        const spreadSheetExporter = this.getSpreadSheetExporter();
        // spreadSheetExporter.setFiltriApplicati(this.filtriPreview);
        spreadSheetExporter
          .export(type, fileName, allRows);
      }
      if (type == 'pdf') {
        const pdfExporter = this.getPDFExporter();
        // pdfExporter.setFiltriApplicati(this.filtriPreview);
        pdfExporter.export(type, fileName, allRows);
      }
      if (type == 'ics') {
        const icsExporter = this.getIcsExporter();
        icsExporter.export(type, fileName, allRows);
      }
      return of(res)
    }));
  }

  initExporter(type: string, columns: any[] = null) {
    if (type == "xls" || type == "xlsx" || type == "csv" || type == "txt") {
      const exporter = this.getSpreadSheetExporter();
      exporter.init({
        map: { map: null, context: this },
        columns: columns ? columns : this.exportColumns,
        author: null
      });
      exporter.initMultiLang(this.translateService, this.appLocale.getCurrentLocale());
    }
    if (type == 'pdf') {
      const exporter = this.getPDFExporter();
      exporter.init({
        map: { map: null, context: this },
        columns: columns ? columns : this.exportColumns,
        maxColumnLenght: 10,
        author: null
      });
      exporter.initMultiLang(this.translateService, this.appLocale.getCurrentLocale());
    }
    if (type == 'ics') {
      console.error("ics export non inizializzato")
    }
  }

  setDataset(data: any[], runParse = true) {
    this.dataset = [];
    this.dataset = [...data];
    if (runParse) {
      this.parseRows();
    }
  }

  setDefaultCRUDSettings(value: CRUDPayloadData) {
    this.defaultCRUDSettings = value;
  }

  private getCRUDSettings() {
    const settings = this.sessionHandler.getPageSettings();
    const defaultSettings = this.defaultCRUDSettings;
    return settings || defaultSettings;
  }

  /**
 * Ottiene filter dall'url
 * @param route
 */
  getFiltersFromURI(route: ActivatedRoute): Filtro[] {
    this.filters = this.sessionHandler.getFiltri(route);
    // console.log("initFiltersURI ", filter);
    if (!this.filters) {
      this.filters = [];
    }
    this.filtersService.setPreviousFilters(this.filters)
    const filtriAttivi = this.filters.filter(el => {
      return el.isAttivo;
    });

    return filtriAttivi;
  }

  /**
   * Aggiorno i filters di crud service guardando ai filtri su filterService
   */
  updateFiltersFromElencoFiltriService() {
    const filters = this.filtersService.getListaFiltriSnapshot();
    if (filters) {
      // console.log("getFiltersFromElencoFiltriService", this.filters, filters);
      this.filters = _.unionWith(this.filters, filters, _.isEqual);
    }
  }


  protected initSearchField() {
    let index = null;
    let nomeCampo = this.searchFilterName;
    if (this.filters) {
      if (this.filters.length > 0) {
        for (let i in this.filters) {
          let filtro: Filtro = this.filters[i];
          if (filtro.nomeCampo === nomeCampo) {
            index = i; //inserisco indice
            break;
          }
        }
        if (index) {
          if (this.filterSearchValue !== null && this.filterSearchValue !== undefined) {
            const searchValue = this.filters[index].extra ? this.filters[index].extra.ricerca : '';
            this.setSearchValue(searchValue);
            this.triggerSearch();
          }
        }
      }
    }
  }

  /**
   * @deprecated
   * @param filtersSettings 
   */
  initFiltersConfig(filtersSettings: any[] = null) {
    // console.log("initFiltriConfig", new Date(), filtersSettings);
    if (!this.filtersSettings) {
      if (filtersSettings) {
        this.filtersSettings = _.cloneDeep(filtersSettings);
      }
      // console.log("initFiltriConfig this.configurazioneFiltri", this.configurazioneFiltri);
      this.filterParser.configFiltri = this.filtersSettings;
      if (this.filtersService)
        this.filtersService.setConfigFiltri(this.filtersSettings);
    }
  }

  /**
   * Centralizzata l'init dei filtri
   * Vengono prese le config in input, dopo aver controllato se son presenti anche filtri dall'URI,
   * aggiorno le config di ciascun filtro, oppure creo dei filtri ex novo dalle config
   * @param filtersSettings 
   */
  initFiltersAndCheckConfigs(filtersSettings: any[] = null) {
    // console.log("initFiltriConfig", new Date(), filtersSettings);
    if (!this.filtersSettings) {//se non ho già delle configurazioni dei filtri
      if (filtersSettings) {
        this.filtersSettings = _.cloneDeep(filtersSettings);
      }
      // console.log("initFiltriConfig this.configurazioneFiltri", this.configurazioneFiltri);
      this.filterParser.configFiltri = this.filtersSettings;
      if (this.activatedRoute) {
        const filtriToApply = this.getFiltersFromURI(this.activatedRoute);
        this.applyFilters(filtriToApply, false);//prepara la prima query
        this.initSearchField();
      }
      if (this.filtersService) {
        this.filtersService.setConfigFiltri(this.filtersSettings);
        this.filtersService.getUpdatedFiltersFromConfig();
        this.updateFiltersFromElencoFiltriService();
      }
      this.initNewFiltersSettedByUserListener();
    }
  }

  /**
   * Si abbona a filtersService per ottenere i filtri aggiornati
   */
  private initNewFiltersSettedByUserListener() {
    this.filtersService.getListaFiltriAsObservable().pipe(
      bufferTime(500)
      , filter(el => el.length > 0)
      , map(el => el[el.length - 1]))
      .subscribe(
        filtri => {
          // console.log("getListaFiltriAsObservable", filtri);
          this.onUpdatedFilters(filtri)
          const filtriAttivi = filtri.filter(el => {
            return el.isAttivo;
          });
          this.applyFilters(filtriAttivi)
        });
  }


  getSearchValue() {
    return this.searchFilterValueSub.asObservable();
  }

  searchValueSnapshot() {
    return this.filterSearchValue;
  }

  /**
   * 
   * @param value 
   */
  setSearchValue(value: any) {
    this.filterSearchValue = value;
    this.searchFilterValueSub.next(value);
  }

  setSearchFilterName(value: string) {
    this.searchFilterName = value;
  }

  triggerSearch() {
    if (this.searchFilterName) {
      let filter: Filtro;
      let indexRicerca = this.filtersService.hasFiltro(this.searchFilterName);
      //inizializzo filtro ricerca
      if (this.filterSearchValue == '' || this.filterSearchValue == null) {
        if (indexRicerca.length == 1) {
          this.filtersService.rimuoviFiltrobyIndice(indexRicerca[0]);
        }
      } else {
        let ricercaParsed = { $regex: this.filterSearchValue, $options: "ig" } //ricerca.toUpperCase(); //RegExp(ricerca,'gi');
        if (indexRicerca.length == 1) {
          //se già presente lo aggiorno
          filter = this.filtersService.getFiltro(indexRicerca[0]);
          filter.valore = ricercaParsed;
          filter.isAttivo = true;
          filter.label = this.searchFilterLabelPrefix + this.filterSearchValue;
          filter.extra = { ricerca: this.filterSearchValue };
          this.filtersService.setFiltrobyIndice(indexRicerca[0], filter);
        } else {
          //sennò lo creo
          filter = {
            nomeCampo: this.searchFilterName,
            valore: ricercaParsed,
            label: this.searchFilterLabelPrefix + this.filterSearchValue,
            isAttivo: true,
            operatoreId: OPERATORI.UGUALE,
            visibile: true,
            deletable: true,
            extra: { ricerca: this.filterSearchValue },
          };
          this.filtersService.aggiungiFiltro(filter);
        }
      }
    }
  }

  applyFilters(filters, executeQuery: boolean = true) {
    const queryParsed = this.filterParser.parse(filters || []);
    let goToPageZero: boolean = false;
    const sameQueryAsLast = _.isEqual(queryParsed, this.lastQueryParsed);
    // console.log(queryParsed, this.lastQueryParsed, sameQueryAsLast);
    if (this.lastQueryParsed && !sameQueryAsLast) {
      goToPageZero = true;
    }
    this.lastQueryParsed = queryParsed;
    // console.log(queryParsed, sameQueryAsLast, this.lastQueryParsed, "goToPageZero", goToPageZero);
    this.query(queryParsed, goToPageZero);
    if (executeQuery) {
      this.runQuery()
    }
  }

  onUpdatedFilters(filters) {
    //aggiorno uri
    if (this.activatedRoute) {
      //this.uriHandler.setFiltriQueryParams(filtri, this.activatedRoute);
      this.sessionHandler.setFiltriQueryParams(filters, this.activatedRoute);
    }
    // let filtriAttivi = filters.filter(el => {
    //   return el.isAttivo;
    // });
    // if (this.hasCRUDSettings && this.toInizialize && filtriAttivi.length == 0) {
    //   this.toInizialize = false;
    // }
  }

  getFiltersService() {
    return this.filtersService;
  }

  getFiltersSettings() {
    return this.filtersSettings;
  }

  getFilters() {
    return this.filters;
  }

  setExportFormats(formats: { id: string; text: string }[]) {
    this.exportFormats = formats;
  }

  getSpreadSheetExporter() {
    return this.spreadSheetsExporter;
  }

  getPDFExporter() {
    return this.pdfExporter;
  }

  getIcsExporter() {
    return this.icsExporter;
  }

  /**
   * 
   */
  getI18nCaptions(){
    if(this.translateService){
      this.searchFilterLabelPrefix = this.translateService.instant('FEPB_CRUD_SEARCHFILTERLABELPX');
    }
  }
}
